export default class LineChart {
  static dayMonthFormatter = new Intl.DateTimeFormat('default', { day: '2-digit', month: 'short' })
  static yearFormatter = new Intl.DateTimeFormat('default', { year: 'numeric' })

  constructor(controller) {
    this.dataset = controller.datasetValue
    this.units = controller.unitsValue
    this.xAxisType = controller.xAxisTypeValue
    this.chart = controller.chart

    this.includesMultipleYears = this.checkIfIncludesMultipleYears()
  }

  checkIfIncludesMultipleYears() {
    if (!this.dataset || this.dataset.length === 0) return false

    try {
      const firstDate = new Date(this.dataset[0][0])
      const lastDate = new Date(this.dataset[this.dataset.length - 1][0])

      return firstDate.getFullYear() !== lastDate.getFullYear()
    } catch (error) {
      console.warn('Error checking if date range includes multiple years:', error)
      return false
    }
  }

  get tooltipDateFormatter() {
    if (!this._tooltipDateFormatter) {
      this._tooltipDateFormatter = new Intl.DateTimeFormat('default',
        this.includesMultipleYears
          ? { day: '2-digit', month: 'short', year: 'numeric' }
          : { day: '2-digit', month: 'short' }
      )
    }
    return this._tooltipDateFormatter
  }

  formatDate(date) {
    if (!this.includesMultipleYears) {
      return this.constructor.dayMonthFormatter.format(date)
    }

    const dayMonth = this.constructor.dayMonthFormatter.format(date)
    const year = this.constructor.yearFormatter.format(date)
    return `${dayMonth}\n${year}`
  }

  formatValueWithUnits(value) {
    if (!this.units || !value) return value

    const separator = this.units === '%' ? '' : ' '
    return `${value}${separator}${this.units}`
  }

  setChart() {
    const useCategory = this.dataset.length <= 7
    const source = useCategory
      ? this.dataset.map(([date, value]) => [this.formatDate(new Date(date)), value])
      : this.dataset.map(([date, value]) => [new Date(date), value])

    const dataset = {
      dimensions: ['label', 'value'],
      source
    }

    const chartLinesStyle = {
      color: '#FF5800',
      width: 3
    }

    const labelStyle = {
      fontFamily: 'Work Sans',
      color: '#80868C',
      fontSize: 14
    }

    const axisLineStyle = {
      color: '#EBEBEB'
    }

    const markLineStyle = {
      type: [4, 4],
      dashOffset: -1,
      color: '#80868C'
    }

    const tooltipAxisPointerLineStyle = {
      type: [4, 4],
      dashOffset: -1,
      color: '#FF5800'
    }

    const tooltipStyle = {
      backgroundColor: '#FFFFFF',
      textStyle: {
        color: '#0A2540'
      }
    }

    const timeLabelFormatter = timestamp => {
      return this.formatDate(timestamp)
    }

    const categoryLabelFormatter = function (value, index) {
      return index % 2 === 1 ? value : ''
    }

    const tooltipCategoryFormatter = (params) => {
      const data = params[0].data
      const label = data[0]
      const value = data[1]
      return `${label}: ${this.formatValueWithUnits(value)}`
    }

    const tooltipTimeFormatter = (params) => {
      const data = params[0].data
      const date = data[0]
      const dateLabel = this.tooltipDateFormatter.format(date)
      const value = data[1]
      return `${dateLabel}: ${this.formatValueWithUnits(value)}`
    }

    const labelFormatters = {
      category: categoryLabelFormatter,
      time: timeLabelFormatter
    }

    const tooltipFormatters = {
      category: tooltipCategoryFormatter,
      time: tooltipTimeFormatter
    }

    const option = {
      dataset,
      grid: {
        top: 50,
        bottom: 45,
        left: 40,
        right: 40,
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          lineStyle: tooltipAxisPointerLineStyle
        },
        formatter: useCategory ? tooltipFormatters['category'] : tooltipFormatters['time'],
        ...tooltipStyle
      },
      xAxis: {
        type: useCategory ? 'category' : 'time',
        offset: 8,
        axisTick: {
          show: false
        },
        axisLabel: {
          interval: 0,
          showMinLabel: true,
          showMaxLabel: true,
          formatter: useCategory ? (value) => value : labelFormatters['time'],
          ...labelStyle
        },
        boundaryGap: false,
        axisLine: {
          lineStyle: axisLineStyle
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: [4, 4],
            dashOffset: -1,
            ...axisLineStyle
          }
        },
      },
      yAxis: {
        type: 'value',
        offset: 5,
        splitNumber: 3,
        minInterval: 1,
        axisLabel: {
          verticalAlign: 'bottom',
          padding: [0, 0, 0, 0],
          formatter: (value) => this.formatValueWithUnits(value),
          ...labelStyle
        },
        splitLine: {
          show: true,
          lineStyle: axisLineStyle
        },
      },
      series: [
        {
          type: 'line',
          encode: { x: 'label', y: 'value' },
          symbol: 'none',
          smooth: true,
          silent: true,
          lineStyle: chartLinesStyle,
          markLine: {
            symbol: ['none', 'none'],
            label: { show: false },
            silent: true,
            data: dataset.source
              .slice(1)
              .map((_, index) => (index % 2 === 1 ? { xAxis: index } : null))
              .filter(Boolean),
            lineStyle: markLineStyle
          }
        }
      ]
    }

    this.chart.setOption(option)
  }
}
